import { useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Template, TemplateCategory } from '@/api/types'
import { PageBar } from '@/app/layout/page-bar'
import { EditTemplateDialog } from '@/features/templates/components/edit-template-dialog'
import { InstallTemplateDialog } from '@/features/templates/components/install-template-dialog'
import { CountsText } from '@/features/templates/components/template-card'
import { TemplateContents } from '@/features/templates/components/template-contents'
import {
  useDeleteTemplate,
  useTemplate,
  useTemplates,
  useUpdateTemplate,
} from '@/features/templates/queries'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { whenText } from '@/shared/lib/when'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

/** One template: what it is for, everything it creates, and the way to use it. */
export function TemplatePage() {
  const { t } = useTranslation()
  const { workspace } = useTenantWorkspace()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar />
      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4 sm:p-6">
        <div className="flex flex-1 flex-col overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-foreground/10">
          <div className="flex items-center gap-3 border-b px-4 py-3 sm:px-6">
            <Button asChild variant="ghost" size="sm">
              <Link to="/templates">
                <ArrowLeft />
                {t('templates.back')}
              </Link>
            </Button>
          </div>
          {workspace === undefined ? (
            <Skeleton className="m-6 h-64" />
          ) : (
            <Detail workspaceId={workspace.id} />
          )}
        </div>
      </div>
    </div>
  )
}

function Detail({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const { templateId } = useParams({ from: '/shell/templates/$templateId' })
  const template = useTemplate(workspaceId, templateId)
  const list = useTemplates(workspaceId)

  if (template.isPending) return <Skeleton className="m-6 h-64" />

  if (template.data === undefined) {
    return (
      <Empty className="flex-1">
        <EmptyHeader>
          <EmptyTitle>{t('templates.notFoundTitle')}</EmptyTitle>
          <EmptyDescription>{t('templates.notFoundDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <TemplateDetail
      workspaceId={workspaceId}
      template={template.data}
      categories={list.data?.categories ?? []}
    />
  )
}

function TemplateDetail({
  workspaceId,
  template,
  categories,
}: {
  workspaceId: number
  template: Template
  categories: readonly TemplateCategory[]
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const remove = useDeleteTemplate()
  const update = useUpdateTemplate()
  const [open, setOpen] = useState<'install' | 'edit' | 'delete' | null>(null)
  const labels = new Map(categories.map((category) => [category.id, category.label]))
  const close = (next: boolean) => {
    if (!next) setOpen(null)
  }

  return (
    <div className="grid flex-1 grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col gap-3">
          <h1 className="text-xl font-medium">{template.title}</h1>
          <div className="flex flex-wrap gap-2">
            {template.source === 'company' && (
              <Badge variant="secondary">{t('templates.companyBadge')}</Badge>
            )}
            {template.archived && (
              <Badge variant="outline">{t('templates.archived')}</Badge>
            )}
            {template.categories.map((category) => (
              <Badge key={category} variant="outline">
                {labels.get(category) ?? category}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            <CountsText counts={template.counts} />
          </p>
        </div>

        {template.archived && (
          <p className="text-sm text-muted-foreground">{t('templates.archivedNotice')}</p>
        )}

        {!template.archived && (
          <Button size="lg" className="h-10 w-full" onClick={() => setOpen('install')}>
            {t('templates.use')}
            <ArrowRight />
          </Button>
        )}

        {template.can_edit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpen('edit')}>
              <Pencil />
              {t('templates.edit')}
            </Button>
            <Button
              variant="outline"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({
                  workspaceId,
                  templateId: template.id,
                  body: { archived: !template.archived },
                })
              }
            >
              {template.archived ? <ArchiveRestore /> : <Archive />}
              {template.archived ? t('templates.unarchive') : t('templates.archive')}
            </Button>
            <Button variant="outline" onClick={() => setOpen('delete')}>
              <Trash2 />
              {t('action.delete')}
            </Button>
          </div>
        )}

        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">{t('templates.about')}</h2>
          <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
            {template.description}
          </p>
        </section>
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">{t('templates.whenToUse')}</h2>
          <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
            {template.when_to_use}
          </p>
        </section>

        {template.created_by !== null && (
          <p className="text-xs text-muted-foreground">
            {t('templates.byline', {
              name: template.created_by.name,
              date: whenText(template.created_on),
            })}
          </p>
        )}
      </div>

      <section className="flex flex-col gap-3 border-t p-6 lg:border-t-0 lg:border-l">
        <h2 className="text-sm font-medium">{t('templates.included')}</h2>
        <TemplateContents summary={template.summary} />
      </section>

      {open === 'install' && (
        <InstallTemplateDialog
          workspaceId={workspaceId}
          template={template}
          open
          onOpenChange={close}
        />
      )}
      {open === 'edit' && (
        <EditTemplateDialog
          workspaceId={workspaceId}
          template={template}
          categories={categories}
          onOpenChange={close}
        />
      )}
      <ConfirmDialog
        open={open === 'delete'}
        onOpenChange={close}
        title={t('templates.deleteTitle', { title: template.title })}
        description={t('templates.deleteDescription')}
        confirmLabel={t('action.delete')}
        destructive
        onConfirm={() =>
          remove.mutate(
            { workspaceId, templateId: template.id },
            { onSuccess: () => void navigate({ to: '/templates' }) },
          )
        }
      />
    </div>
  )
}
